import { StyleSheet, Text } from 'react-native';

import { colors } from '@/constants/theme';

export interface MergeTagPillProps {
  /** The merge token (e.g. "first_name") — drives the a11y label, not the visible text. */
  token: string;
  /** The substituted value (e.g. "Sarah") that the recipient will see. */
  value: string;
}

/**
 * Read-only render of a substituted merge tag inside the live-preview block.
 * Inline-styled <Text> (not a Pill <View>) so it sits on the text baseline
 * instead of floating above it.
 *
 * Hard rule (plan D2): never use this inside a `<TextInput>` — RN doesn't
 * support styled inline children in TextInput.
 *
 * Empty values render as a muted em-dash so the gap is visible at a glance
 * (e.g., a recipient with no last_name) without breaking the line layout.
 */
export function MergeTagPill({ token, value }: MergeTagPillProps) {
  const isEmpty = value.length === 0;
  const display = isEmpty ? '—' : value;
  return (
    <Text
      style={isEmpty ? styles.emptyTag : styles.tag}
      accessibilityLabel={`merge tag: ${token} = ${isEmpty ? 'empty' : value}`}
    >
      {display}
    </Text>
  );
}

const styles = StyleSheet.create({
  tag: {
    backgroundColor: 'rgba(120, 193, 68, 0.18)',
    color: colors.deepGreen,
    fontWeight: '600',
  },
  emptyTag: {
    color: colors.textMuted,
    fontStyle: 'italic',
  },
});
